import React, { useEffect, useState } from "react";
import styled from "styled-components";
import theme from "../../../../assets/theme";
import {
  Grid,
  Paper,
  Typography,
  Modal,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  makeStyles,
} from "@material-ui/core";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import { Formik } from "formik";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import { useSnackbar } from "notistack";
import { FormLabel } from "../../NoticeGeneration/styles";
import FormField from "../../../common/FormField/FormField";
import SystemService from "../../../../services/SystemService";
import queryString from "query-string";
import AuthService from "../../../../services/AuthService";
import { signerGatewayValidationSchema } from "../../../../helpers/validationSchema";
import { s3Upload } from "../../../common/FileUploadS3";
import { getFileName } from "../../../../helpers/functions";
import useLoader from "../../../../hooks/useLoader";
import {
  StyledAutocomplete,
  OptionContainer,
  OptionContent,
  OptionCheck,
  OptionName,
  OptionEmail,
} from "../../NoticeGeneration/component/CustomSelect";

const useStyles = makeStyles((themes) => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
  },
  root: {
    margin: 15,
    [themes.breakpoints.up(640)]: {
      margin: "0px 46px",
    },
  },
  radioRoot: {
    margin: 15,
    [themes.breakpoints.up(640)]: {
      margin: "0px 26px",
    },
  },
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    "& li[data-option-index]": {
      padding: "12px 16px !important",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "#f0f9ff",
        paddingLeft: "20px !important",
      },
    },
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
    padding: "16px",
    fontSize: "14px",
  },
  paper: {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
    borderRadius: "8px !important",
    border: "1px solid #e2e8f0 !important",
  },
}));

const PreSignIn = ({ signIn, setSignIn, MTRef }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [neutralList, setNeutralList] = useState([
    { label: "None", value: "none" },
  ]);
  const [loading, setLoading] = useState(false);
  const { setLoader } = useLoader();
  const classes = useStyles();

  useEffect(() => {
    async function getNeutralList() {
      try {
        setLoading(true);
        let params = {
          page: 1,
          perPage: 1000,
          role: "mediator",
        };
        let stringParams = `?${queryString.stringify(params)}`;
        const response = await SystemService.getProfiles(stringParams);
        if (response?.data) {
          setNeutralList(
            response.data.map((item) => ({
              label: item.name,
              value: item.id,
              email: item.email,
            }))
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    getNeutralList();
  }, []);

  const inputs = [
    {
      label: "Signature Mode",
      name: "SignatureMode",
      type: "select",
      menuItems: [
        { label: "Place signature", value: 3, disabled: false },
        { label: "AADHAAR based document signing", value: 12 },
      ],
    },
    {
      label: "Signature Position",
      name: "SignaturePosition",
      type: "select",
      menuItems: [
        { label: "Top left", value: "Top-Left" },
        { label: "Top center", value: "Top-Center" },
        { label: "Top right", value: "Top-Right" },
        { label: "Middle left", value: "Middle-Left" },
        { label: "Middle right", value: "Middle-Right" },
        { label: "Middle center", value: "Middle-Center" },
        { label: "Bottom left", value: "Bottom-Left" },
        { label: "Bottom right", value: "Bottom-Right" },
        { label: "Bottom center", value: "Bottom-Center" },
        { label: "Customize", value: "Customize" },
      ],
      required: true,
    },
    // This one will be replaced with searchable Autocomplete
    {
      label: "Arbitrator",
      name: "arbitratorName",
      type: "select",
      menuItems: neutralList,
      required: true,
    },
  ];

  const mcx_inputs = [
    {
      label: "Signature Mode",
      name: "SignatureMode",
      type: "select",
      menuItems: [
        { label: "Place signature", value: 3, disabled: false },
        { label: "AADHAAR based document signing", value: 12 },
      ],
    },
    {
      label: "Signature Position",
      name: "SignaturePosition",
      type: "select",
      menuItems: [
        { label: "Top left", value: "Top-Left" },
        { label: "Top center", value: "Top-Center" },
        { label: "Top right", value: "Top-Right" },
        { label: "Middle left", value: "Middle-Left" },
        { label: "Middle right", value: "Middle-Right" },
        { label: "Middle center", value: "Middle-Center" },
        { label: "Bottom left", value: "Bottom-Left" },
        { label: "Bottom right", value: "Bottom-Right" },
        { label: "Bottom center", value: "Bottom-Center" },
        { label: "Customize", value: "Customize" },
      ],
      required: true,
    },
    {
      label: "Arbitrator Name",
      name: "neutralName",
      type: "text",
      required: true,
    },
    {
      label: "Arbitrator Email",
      name: "arbitratorEmailId",
      type: "text",
      required: true,
    },
  ];

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "please wait..." });
      let arbitrator = neutralList.find(
        ({ value }) => value === values?.arbitratorName
      );
      const payload = {
        arbitratorName: values?.neutralName
          ? values?.neutralName
          : arbitrator?.label || "None",
        SignatureMode: values?.SignatureMode,
        SignaturePosition: values.SignaturePosition,
        arbitratorId: values.arbitratorName || 1,
        arbitratorEmailId: values?.arbitratorEmailId
          ? values?.arbitratorEmailId
          : arbitrator?.email || "",
        CustomizeCoordinates: values?.CustomizeCoordinates,
        internalReferenceNumber: values?.internalReferenceNumber,
        fileBase64: values.file,
      };
      const response = await AuthService.createEsign(payload);
      if (response.message) {
        enqueueSnackbar(response.message, { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar(error?.message || "Something went wrong", {
        variant: "error",
      });
    } finally {
      setSignIn({ state: false });
      setLoader({ state: false });
      MTRef.current.onQueryChange("");
    }
  }

  return (
    <Modal
      open={signIn?.state}
      onClose={() => setSignIn({ state: false })}
      disableRestoreFocus={true}
    >
      <DrawerContainer>
        <Container>
          <Paper elevation={3} style={{ padding: "16px" }}>
            <HeadingContainer>
              <Heading>Signer Gateway</Heading>
            </HeadingContainer>
            <Formik
              initialValues={{
                Name: "",
                fileName: "",
                SignatureMode: 12,
                SignaturePosition: "",
                arbitratorName: "",
                file: "",
                CustomizeCoordinates: "",
                typeOfAction: 1,
                arbitratorEmailId: "",
                neutralName: "",
                internalReferenceNumber: "",
              }}
              enableReinitialize
              validateOnBlur
              validationSchema={signerGatewayValidationSchema}
              validateOnChange
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
                handleBlur,
                isValid,
              }) => (
                <div>
                  {loading ? (
                    <div className="loader">
                      <CircularProgress />
                    </div>
                  ) : (
                    <>
                      <div className="status-list">
                        <Grid
                          className={classes.radioRoot}
                          item
                          xs={12}
                          spacing={2}
                          style={{ marginTop: "5px", padding: 16 }}
                        >
                          <RadioGroup
                            style={{
                              flexDirection: "row",
                              marginLeft: "10px",
                            }}
                            name={"typeOfAction"}
                            value={values.typeOfAction}
                            onChange={(e) => {
                              setFieldValue(
                                "typeOfAction",
                                parseInt(e.target.value)
                              );
                            }}
                          >
                            {[
                              { label: "JustAct arbitrators", value: 1 },
                              { label: "Other arbitrators", value: 0 },
                            ].map((radio, key) => (
                              <FormControlLabel
                                key={key}
                                classes={{ label: classes.formLabel }}
                                value={radio.value}
                                control={
                                  <Radio
                                    size="medium"
                                    style={{ color: "#00838c" }}
                                  />
                                }
                                label={radio.label}
                              />
                            ))}
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} spacing={2} style={{ padding: 16 }}>
                          {(values.typeOfAction ? inputs : mcx_inputs)
                            .filter((input) =>
                              values.typeOfAction
                                ? input.name !== "arbitratorName"
                                : true
                            )
                            .map((input, index) => (
                              <FormField
                                key={index}
                                menuItems={input?.menuItems}
                                {...{
                                  touched,
                                  errors,
                                  input,
                                  values,
                                  handleChange,
                                  handleBlur,
                                  setFieldValue,
                                }}
                              />
                            ))}
                          {values.typeOfAction === 1 && (
                            <div
                              className="form-fields"
                              style={{
                                width: "100%",
                                paddingTop: 5,
                                marginBottom: 10,
                              }}
                            >
                              <FormLabel>
                                Arbitrator{" "}
                                <span className="required-star"> *</span>
                              </FormLabel>
                              <StyledAutocomplete
                                classes={{
                                  listbox: classes.listbox,
                                  noOptions: classes.noOptions,
                                  paper: classes.paper,
                                }}
                                options={neutralList}
                                getOptionLabel={(option) => option.label || ""}
                                value={
                                  neutralList.find(
                                    (opt) => opt.value === values.arbitratorName
                                  ) || null
                                }
                                onChange={(event, newValue) => {
                                  setFieldValue(
                                    "arbitratorName",
                                    newValue ? newValue.value : ""
                                  );
                                }}
                                loading={loading}
                                noOptionsText="No arbitrators found"
                                renderInput={(params) => (
                                  <CustomInputField
                                    {...params}
                                    variant="outlined"
                                    placeholder="Search arbitrator..."
                                    style={{ marginTop: 6, marginBottom: 10 }}
                                    error={
                                      touched.arbitratorName &&
                                      Boolean(errors.arbitratorName)
                                    }
                                    helperText={
                                      touched.arbitratorName &&
                                      errors.arbitratorName
                                    }
                                  />
                                )}
                                PaperProps={{
                                  style: {
                                    borderRadius: 8,
                                    boxShadow:
                                      "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                  },
                                }}
                                ListboxProps={{
                                  style: {
                                    fontFamily: "OpenSans-Regular, sans-serif",
                                    fontSize: "14px",
                                    color: "#293461",
                                    padding: "4px 0",
                                  },
                                }}
                                renderOption={(option) => (
                                  <OptionContainer>
                                    <OptionContent>
                                      <OptionName>{option.label}</OptionName>
                                      <OptionEmail>{option.email}</OptionEmail>
                                    </OptionContent>
                                    <OptionCheck>✓</OptionCheck>
                                  </OptionContainer>
                                )}
                              />
                            </div>
                          )}
                          <div style={{ marginTop: 10, marginBottom: 10 }}>
                            <FormLabel>
                              Uploaded File{" "}
                              <span className="required-star"> *</span>
                            </FormLabel>
                            <CustomFileUpload
                              error={touched["file"] && errors["file"]}
                              setFile={(file) =>
                                s3Upload(
                                  file,
                                  "file",
                                  setFieldValue,
                                  ["application/pdf"],
                                  setLoader
                                )
                              }
                              width={"100%"}
                              accept={".pdf"}
                              file={{ name: getFileName(values["file"]) }}
                            />
                          </div>
                          <div style={{ marginTop: 10 }}>
                            {[
                              {
                                label: "Internal reference text",
                                name: "internalReferenceNumber",
                                type: "text",
                                required: false,
                              },
                            ].map((input, index) => {
                              return (
                                <FormField
                                  key={index}
                                  {...{
                                    touched,
                                    errors,
                                    input,
                                    values,
                                    handleChange,
                                    handleBlur,
                                    setFieldValue,
                                  }}
                                />
                              );
                            })}
                          </div>
                          {[
                            {
                              label: "Customize Coordinates",
                              name: "CustomizeCoordinates",
                              type: "text",
                              placeholder:
                                "7,10,60,120 (Left,Top,Width,Height)",
                              discription:
                                "Please refer this link to get the Coordinates",
                              link: "https://coordinates-int.emsigner.com/",
                            },
                          ].map((input, index) => {
                            return (
                              <FormField
                                key={index}
                                {...{
                                  touched,
                                  errors,
                                  input,
                                  values,
                                  handleChange,
                                  handleBlur,
                                  setFieldValue,
                                }}
                              />
                            );
                          })}
                        </Grid>
                      </div>
                      <FormContainer>
                        <Flex>
                          <HalfWidth>
                            <PrimaryOutlinedCloseCTAButton
                              style={{ padding: "10px 10px" }}
                              onClick={() => setSignIn({ state: false })}
                            >
                              Cancel
                            </PrimaryOutlinedCloseCTAButton>
                          </HalfWidth>

                          <HalfWidth>
                            <PrimaryCTAButton
                              style={{ padding: "10px 10px" }}
                              onClick={handleSubmit}
                              disabled={!isValid}
                            >
                              Submit
                            </PrimaryCTAButton>
                          </HalfWidth>
                        </Flex>
                      </FormContainer>
                    </>
                  )}
                </div>
              )}
            </Formik>
          </Paper>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default PreSignIn;

// Styled components remain exactly the same
const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
  & .status-list {
    border: 4px solid transparent;
    border-radius: 8px;
    margin-left: 10px;
    height: 450px;
    overflow: auto;
  }
  & .loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    justify-content: center;
  }
`;

const FormContainer = styled.div`
  padding-top: 5px;
  padding-left: 28px;
  padding-right: 54px;
  padding-bottom: 6px;
`;

const Heading = styled(Typography)(() => ({
  backgroundColor: "#A5BFCC",
  color: "#fff",
  padding: "8px",
  fontFamily: theme.fonts.primaryFontBold,
  borderRadius: "5px",
  textAlign: "center",
}));

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 656px;
  }
`;

const HeadingContainer = styled.div`
  padding-right: 15px;
  padding-left: 15px;
  padding-top: 10px;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 47px;
  padding-left: 24px;
  padding-right: 24px;
`;

const HalfWidth = styled.div`
  width: 48%;
`;
